import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Badge } from "./ui/badge";
import { WarningCircle, CheckCircle } from "@phosphor-icons/react";

const fmt = (n) => (n == null ? "—" : Number(n).toLocaleString("en-IN", { maximumFractionDigits: 2 }));

export function PayrollUploadPreview({ preview, fileName, onCancel, onConfirm, busy }) {
  if (!preview) return null;
  const t = preview.totals || {};
  const stats = [
    ["Employees", preview.valid_rows, `${preview.to_create} new · ${preview.to_update} update`],
    ["Total gross", fmt(t.gross), `${t.employees_with_lop} with LOP · ${t.lop_days} lost day(s)`],
    ["LOP deduction", fmt(t.lop_deduction), "salary withheld for lost days"],
    ["Total deductions", fmt(t.deductions), "PF · ESIC · PT · TDS · other"],
    ["Total net pay", fmt(t.net_pay), `CTC ${fmt(t.cost_to_company)}`],
  ];
  return (
    <Dialog open onOpenChange={(v) => !v && onCancel()}>
      <DialogContent className="max-w-6xl" data-testid="payroll-upload-preview">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Review upload · {preview.period_month}</DialogTitle>
          <div className="text-xs text-zinc-500">{fileName} · {preview.total_rows} row(s) parsed. Nothing is saved until you confirm.</div>
        </DialogHeader>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="payroll-preview-totals">
          {stats.map(([l, v, s]) => (
            <div key={l} className="border border-zinc-200 bg-zinc-50 rounded-sm p-3">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">{l}</div>
              <div className="font-display text-xl font-bold font-mono-plex">{v}</div>
              <div className="text-[10px] text-zinc-500">{s}</div>
            </div>
          ))}
        </div>
        {preview.errors?.length > 0 && (
          <div className="border border-amber-300 bg-amber-50 rounded-sm p-3 text-xs text-amber-900 max-h-24 overflow-y-auto" data-testid="payroll-preview-errors">
            <div className="font-semibold flex items-center gap-1"><WarningCircle size={14} /> {preview.errors.length} row(s) will be skipped</div>
            {preview.errors.map((e, i) => <div key={i} className="font-mono-plex">{e}</div>)}
          </div>
        )}
        <div className="max-h-[45vh] overflow-auto border border-zinc-200 rounded-sm">
          <table className="hr-table text-xs">
            <thead><tr><th>#</th><th>Employee</th><th>Present / Working</th><th>Lost days</th><th>Basic</th><th>Gross</th><th>LOP deduction</th><th>Deductions</th><th>Net pay</th><th></th></tr></thead>
            <tbody>
              {preview.rows.map((r) => (
                <tr key={r.line} className={r.lop_days ? "bg-amber-50/40" : ""} data-testid={`payroll-preview-row-${r.employee_code}`}>
                  <td className="font-mono-plex text-zinc-400">{r.line}</td>
                  <td><div>{r.employee_name}</div><div className="font-mono-plex text-zinc-500">{r.employee_code}</div></td>
                  <td className="font-mono-plex">{r.present_days} / {r.working_days}</td>
                  <td className="font-mono-plex">{r.lop_days ? <span className="text-amber-800 font-semibold">{r.lop_days}</span> : "—"}</td>
                  <td className="font-mono-plex">{fmt(r.basic)}</td>
                  <td className="font-mono-plex">{fmt(r.gross)}</td>
                  <td className="font-mono-plex text-amber-800">{r.lop_deduction ? `− ${fmt(r.lop_deduction)}` : "—"}</td>
                  <td className="font-mono-plex">{fmt(r.deductions)}</td>
                  <td className="font-mono-plex font-semibold text-emerald-700">{fmt(r.net_pay)}</td>
                  <td><Badge variant="outline" className={`rounded-sm ${r.action === "update" ? "bg-blue-50 text-blue-800 border-blue-200" : "bg-emerald-50 text-emerald-800 border-emerald-200"}`}>{r.action}</Badge></td>
                </tr>
              ))}
              {preview.rows.length === 0 && <tr><td colSpan={10} className="text-center py-6 text-zinc-500">No valid rows — fix the errors above and upload again.</td></tr>}
            </tbody>
          </table>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} className="rounded-sm" data-testid="payroll-preview-cancel">Cancel</Button>
          <Button onClick={onConfirm} disabled={busy || !preview.rows.length} className="btn-zenith rounded-sm" data-testid="payroll-preview-confirm">
            <CheckCircle size={14} className="mr-1" /> {busy ? "Importing…" : `Commit ${preview.valid_rows} payslip(s) to run`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
